/** @format */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Dialog,
  DialogTitle,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Tooltip,
} from '@mui/material';
import PropTypes from 'prop-types';
import { cloneElement, useState } from 'react';

export function documentUrl(doc) {
  return doc?.url || doc?.file || null;
}

// One button per document type: downloads the document, or lets the user pick one when there
// are several. Without any document, the button stays grey and disabled.
export default function DocumentButton({ docs, label, icon, children }) {
  const [open, setOpen] = useState(false);
  const single = docs.length === 1 ? docs[0] : null;
  const stop = (e) => e.stopPropagation();
  const linkProps = single
    ? {
        component: 'a',
        href: documentUrl(single),
        target: '_blank',
        rel: 'noopener noreferrer',
        download: true,
      }
    : {};
  const title = docs.length
    ? `${label}${docs.length > 1 ? ` (${docs.length})` : ` : ${single.name}`}`
    : `Pas de ${label.toLowerCase()}`;
  const onClick = (e) => {
    stop(e);
    if (docs.length > 1) setOpen(true);
  };

  return (
    <>
      <Tooltip title={title}>
        {/* span: the tooltip still shows on a disabled button */}
        <Box component="span" sx={{ display: 'inline-flex' }}>
          {children ? (
            cloneElement(children, {
              ...linkProps,
              disabled: !docs.length,
              onClick,
              onKeyDown: stop,
            })
          ) : (
            <IconButton
              {...linkProps}
              disabled={!docs.length}
              color="primary"
              aria-label={title}
              onClick={onClick}
              onKeyDown={stop}
              sx={{
                width: 44,
                height: 44,
                '&.Mui-disabled': { color: 'grey.400' },
              }}
            >
              <FontAwesomeIcon icon={icon} />
            </IconButton>
          )}
        </Box>
      </Tooltip>
      {docs.length > 1 && (
        <Dialog
          open={open}
          onClose={() => setOpen(false)}
          onClick={stop}
          maxWidth="xs"
          fullWidth
          PaperProps={{ sx: { borderRadius: '16px' } }}
        >
          <DialogTitle>{label} : choisir le document</DialogTitle>
          <List sx={{ pt: 0, pb: 1 }}>
            {docs.map((doc) => (
              <ListItemButton
                key={doc.id}
                component="a"
                href={documentUrl(doc)}
                target="_blank"
                rel="noopener noreferrer"
                download
                onClick={() => setOpen(false)}
                sx={{ minHeight: 44, gap: 1.5, px: 3 }}
              >
                <FontAwesomeIcon icon={icon} fixedWidth />
                <ListItemText primary={doc.name || 'Sans nom'} />
              </ListItemButton>
            ))}
          </List>
        </Dialog>
      )}
    </>
  );
}

DocumentButton.propTypes = {
  docs: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.number, name: PropTypes.string }),
  ).isRequired,
  label: PropTypes.string.isRequired,
  icon: PropTypes.shape({}).isRequired,
  children: PropTypes.element,
};

DocumentButton.defaultProps = { children: null };
